import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { describeError, useRegister } from '../api';
import { TelegramLoginPanel } from './TelegramLoginPanel';
import { AuthDivider, AuthSwitch } from './AuthDialogParts';
import type { AuthMode } from '../auth-dialog-context';

/**
 * Mirrors the server's rules. This is a convenience, not a control — the API
 * validates the same things again, because anything checked only in the browser
 * is not checked at all.
 */
const schema = z
  .object({
    userName: z
      .string()
      .min(3, 'En az 3 karakter olmalı.')
      .max(32, 'En fazla 32 karakter olabilir.')
      .regex(/^[a-zA-Z0-9_]+$/, 'Yalnızca harf, rakam ve alt çizgi kullanılabilir.'),
    email: z.email('Geçerli bir e-posta adresi girin.'),
    password: z.string().min(10, 'Parola en az 10 karakter olmalı.'),
    passwordConfirm: z.string(),
  })
  .refine((values) => values.password === values.passwordConfirm, {
    message: 'Parolalar eşleşmiyor.',
    path: ['passwordConfirm'],
  });

type FormValues = z.infer<typeof schema>;

interface RegisterFormProps {
  onAuthenticated: () => void;
  onModeChange: (mode: AuthMode) => void;
}

export function RegisterForm({ onAuthenticated, onModeChange }: RegisterFormProps) {
  const registerUser = useRegister();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // mutate, not mutateAsync. react-hook-form rethrows whatever the submit
  // handler throws, and the `void onSubmit(event)` below discards it — so every
  // rejected registration would surface as an unhandled rejection, which also
  // fails a Vitest run outright. The error still renders: it comes from
  // `isError`.
  const onSubmit = handleSubmit((values) =>
    registerUser.mutate(
      { userName: values.userName, email: values.email, password: values.password },
      { onSuccess: onAuthenticated },
    ),
  );

  return (
    <div className="flex flex-col gap-5 pb-2">
      <TelegramLoginPanel onSignedIn={onAuthenticated} />

      <AuthDivider />

      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        {registerUser.isError ? (
          <Alert tone="error">{describeError(registerUser.error)}</Alert>
        ) : null}

        <Field
          label="Kullanıcı adı"
          autoComplete="username"
          placeholder="kullaniciadi"
          error={errors.userName?.message}
          {...register('userName')}
        />

        <Field
          label="E-posta"
          type="email"
          autoComplete="email"
          placeholder="ornek@eposta.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <Field
          label="Parola"
          type="password"
          autoComplete="new-password"
          hint="En az 10 karakter. Uzun bir cümle, karmaşık bir kelimeden iyidir."
          error={errors.password?.message}
          {...register('password')}
        />

        <Field
          label="Parola (tekrar)"
          type="password"
          autoComplete="new-password"
          error={errors.passwordConfirm?.message}
          {...register('passwordConfirm')}
        />

        <Button type="submit" loading={registerUser.isPending}>
          Hesap oluştur
        </Button>
      </form>

      <AuthSwitch
        question="Zaten hesabın var mı?"
        action="Giriş yap"
        onClick={() => onModeChange('login')}
      />

      <p className="text-center text-xs text-surface-600">
        Kayıt olarak 18 yaşından büyük olduğunu onaylıyorsun.
      </p>
    </div>
  );
}
